import { useUpdateRenewalStatusMutation } from "@qw/graphql";
import { cn, Popover, PopoverContent, PopoverTrigger } from "@qw/ui";
import { useState } from "react";

// Import RenewalStatus constants from backend
const RenewalStatus = {
  GREEN: "green",
  YELLOW: "yellow",
  RED: "red",
} as const;

type RenewalStatusValue = (typeof RenewalStatus)[keyof typeof RenewalStatus];

interface RenewalStatusPickerProps {
  opportunityId: string;
  currentStatus: string | null | undefined;
}

const STATUS_CONFIG = {
  [RenewalStatus.GREEN]: { color: "bg-green-500" },
  [RenewalStatus.YELLOW]: { color: "bg-yellow-500" },
  [RenewalStatus.RED]: { color: "bg-red-500" },
};

export const RenewalStatusPicker = ({ opportunityId, currentStatus }: RenewalStatusPickerProps) => {
  const [open, setOpen] = useState(false);
  const [localStatus, setLocalStatus] = useState(currentStatus);
  const [updateRenewalStatus] = useUpdateRenewalStatusMutation();

  const handleStatusChange = async (status: RenewalStatusValue) => {
    setOpen(false);

    try {
      // Send the update to the backend
      await updateRenewalStatus({
        variables: {
          input: {
            opportunityId,
            status,
          },
        },
        refetchQueries: ["BrokerOpportunities", "BrokerRenewals"],
      });

      // Only update the UI on success
      setLocalStatus(status);
    } catch {
      // do nothing
    }
  };

  const displayStatus = localStatus ?? currentStatus;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex items-center justify-center size-8 hover:opacity-70 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={cn(
              "size-2 rounded-full cursor-pointer bg-gray-400",
              displayStatus === RenewalStatus.GREEN && "bg-green-500",
              displayStatus === RenewalStatus.YELLOW && "bg-yellow-500",
              displayStatus === RenewalStatus.RED && "bg-red-500"
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="end">
        <div className="flex gap-2">
          {Object.entries(STATUS_CONFIG).map(([status, { color }]) => (
            <button
              key={status}
              onClick={() => handleStatusChange(status as RenewalStatusValue)}
              className={cn(
                "p-2 rounded hover:bg-accent transition-colors",
                displayStatus === status && "bg-accent ring-2 ring-primary"
              )}
            >
              <div className={cn("size-3 rounded-full", color)} />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
